<template>
  <div v-if="material" class="grid h-full grid-cols-1 grid-rows-[auto_1fr]">
    <!-- Header -->
    <BaseDetailsPreviewEditHeader
      :is-horizontal-layout="isHorizontalLayout"
      :title="modifiedMaterial?.smallName ? modifiedMaterial.smallName : '-'"
      link-name="materials-management"
      :mode="mode"
      :is-modified="isModified"
      :is-edit-active="material.isFromManager || false"
      @edit-mode="onEditMode"
      @cancel="onCancelEdit"
      @save="onSaveChanges"
      @horizontal-layout="(value: boolean) => (isHorizontalLayout = value)" />

    <AppResizableContainer
      :layout="isHorizontalLayout ? 'vertical' : 'horizontal'"
      :minSize="300"
      class="overflow-auto">
      <template #headerTitle v-if="isHorizontalLayout">
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap gap-4 items-center pl-4 -mb-px">
            <li
              v-for="tab in section1Tabs"
              :key="tab.name"
              @click="selectedTab = tab.name"
              class="py-1.5 cursor-pointer"
              :class="[
                selectedTab === tab.name ? 'border-b-2 border-theme-500' : '',
                tab.validator &&
                tab.validator.some((item) => {
                  return $v[item].required.$invalid;
                })
                  ? 'text-red-500 !border-red-500'
                  : '',
              ]">
              <p>
                {{ tab.label }}
              </p>
            </li>
          </ul>
        </nav>
      </template>
      <template #headerContent>
        <!-- Horizontal Layout -->
        <template v-if="isHorizontalLayout">
          <component
            :is="currentComponent"
            v-model="modifiedMaterial"
            :material="material"
            :mode="mode"
            :isHorizontalLayout="isHorizontalLayout"
            :key="material?.id"
            :validations="$v" />
        </template>

        <!-- Vertical Layout -->
        <template v-else>
          <PerfectScrollbar class="py-4 space-y-2 w-full h-full" ref="scrollbarId">
            <BaseAccordion
              v-for="tab in section1Tabs"
              :key="tab.name"
              :name="tab.label"
              :error="
                tab.validator &&
                tab.validator.some((item) => {
                  return $v[item].required.$invalid;
                })
              ">
              <MaterialDetailsTabProperties
                v-if="tab.name === 'properties'"
                v-model="modifiedMaterial"
                :material="material"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout"
                :validations="$v" />

              <MaterialDetailsTabDimensions
                v-else-if="tab.name === 'dimensions'"
                v-model="modifiedMaterial"
                :material="material"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout"
                :validations="$v" />

              <MaterialDetailsTabBalance
                v-else-if="tab.name === 'balance'"
                v-model="modifiedMaterial"
                :material="material"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout" />

              <MaterialDetailsTabOther
                v-else
                v-model="modifiedMaterial"
                :material="material"
                :mode="mode"
                :isHorizontalLayout="isHorizontalLayout" />
            </BaseAccordion>
          </PerfectScrollbar>
        </template>
      </template>

      <!-- Tables Section -->
      <template #detailsTitle>
        <nav
          role="section-nav"
          class="flex justify-between px-6 text-sm font-medium text-center text-gray-500 sm:px-0 min-h-9">
          <ul class="flex flex-wrap gap-4 items-center pl-4 -mb-px">
            <li
              v-for="tab in detailsTabs"
              :key="tab.name"
              @click="selectedDetailsTab = tab.name"
              class="py-1.5 cursor-pointer"
              :class="selectedDetailsTab === tab.name ? 'border-b-2 border-theme-500' : ''">
              <p>
                {{ tab.label }}
              </p>
            </li>
          </ul>
        </nav>
      </template>

      <template #detailsContent>
        <MaterialDetailsTableCompanyAlterCodes
          v-if="currentDetailsComponent?.name == 'companyAlterCodes'"
          :mode="mode"
          :material-id="material.id"
          :reset-state="resetTablesState"
          :modified-data="modifiedMaterial?.materialAlterCodes"
          @saveMaterialAlterCode="onSaveMaterialAlterCode" />

        <MaterialDetailsTableCompetitorAlterCodes
          v-else
          :mode="mode"
          :material-id="material.id"
          :reset-state="resetTablesState"
          :modified-data="modifiedMaterial?.competitorMaterials"
          @saveCompetitorMaterialAlterCode="onSaveCompetitorMaterials" />
      </template>
    </AppResizableContainer>

    <BaseDrawerConfirm
      :is-active="confirmModalActive"
      @cancel="confirmModalActive = false"
      @confirm="onDiscardChanges" />
  </div>
</template>

<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useLookupsStore } from '@/stores/lookups';
import { addOrEditMaterial, getMaterialById } from '@/services/api/materials';
import AppResizableContainer from '@/components/common/AppResizableContainer.vue';
import BaseDetailsPreviewEditHeader from '@/components/common/BaseDetailsPreviewEditHeader.vue';
import MaterialDetailsTabProperties from '@/components/materialManagement/MaterialDetailsTabProperties.vue';
import MaterialDetailsTabDimensions from '@/components/materialManagement/MaterialDetailsTabDimensions.vue';
import MaterialDetailsTabOther from '@/components/materialManagement/MaterialDetailsTabOther.vue';
import MaterialDetailsTabBalance from '@/components/materialManagement/MaterialDetailsTabBalance.vue';
import BaseDrawerConfirm from '@/components/common/BaseDrawerConfirm.vue';
import MaterialDetailsTableCompanyAlterCodes from '@/components/materialManagement/MaterialDetailsTableCompanyAlterCodes.vue';
import MaterialDetailsTableCompetitorAlterCodes from '@/components/materialManagement/MaterialDetailsTableCompetitorAlterCodes.vue';
import BaseAccordion from '@/components/common/BaseAccordion.vue';
import type { CompetitorMaterial, Material, MaterialAlterCode } from '@/types/Materials';
import type { PerfectScrollbarExpose } from 'vue3-perfect-scrollbar';
import useVuelidate from '@vuelidate/core';
import { helpers, maxLength, required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { deepCopy } from '@/utils/data';
import { useToastStore } from '@/stores/toast';

const route = useRoute();
const router = useRouter();
const material = ref<Material>();
const modifiedMaterial = ref<Material>();
const { t: $t } = useI18n();

const isHorizontalLayout = ref(true);
const mode = ref<'view' | 'edit'>('view');

const resetTablesState = ref(false);
const {
  getCompaniesLookup,
  getMaterialTypesLookup,
  getVatCategoriesLookup,
  getMwAccountGroupsLookup,
  getColorsLookup,
  getSizesLookup,
  getStylesLookup,
  getMeasurementUnitsLookup,
  getAlterCodeTypesLookup,
  getCompetitorsLookup,
} = useLookupsStore();

onMounted(async () => {
  await getCompaniesLookup();
  await getMaterialTypesLookup();
  await getVatCategoriesLookup();
  await getMwAccountGroupsLookup();
  await getColorsLookup();
  await getSizesLookup();
  await getStylesLookup();
  await getMeasurementUnitsLookup();
  await getAlterCodeTypesLookup();
  await getCompetitorsLookup();
  await loadData();
});

async function loadData() {
  const res = await getMaterialById(route.params.id as string);
  if (res) {
    material.value = res as Material;
    mode.value = 'view';
  } else {
    material.value = {
      id: route.params.id as string,
      isFromManager: true,
      isActive: true,
      code: '',
      name: '',
      motherCodeId: route.params.id as string,
      company: {
        id: '',
        name: '',
        code: '',
      },
      materialAlterCodes: [],
    };
    mode.value = 'edit';
  }
  modifiedMaterial.value = deepCopy<Material>(material.value);
}

const section1Tabs = ref([
  {
    name: 'properties',
    label: $t('competitor.properties'),
    component: markRaw(MaterialDetailsTabProperties),
    validator: ['company', 'name', 'code', 'vatCategory', 'materialType'],
  },
  {
    name: 'dimensions',
    label: $t('material.dimensions'),
    component: markRaw(MaterialDetailsTabDimensions),
    validator: ['mwAccountGroup'],
  },
  {
    name: 'balance',
    label: $t('salesDocuments.materialBalance'),
    component: markRaw(MaterialDetailsTabBalance),
  },
  { name: 'other', label: $t('common.other'), component: markRaw(MaterialDetailsTabOther) },
]);

const validationModel = ref({
  company: modifiedMaterial.value?.companyId || '',
  code: modifiedMaterial.value?.code || '',
  name: modifiedMaterial.value?.name || '',
  vatCategory: modifiedMaterial.value?.vatCategoryId || '',
  materialType: modifiedMaterial.value?.materialTypeId || '',
  mwAccountGroup: modifiedMaterial.value?.mwAccountGroupId || '',
});
const rules = computed(() => ({
  company: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  code: {
    required: helpers.withMessage($t('messages.errors.required'), required),
    maxLength: helpers.withMessage(`${$t('messages.errors.maxValue')} 50`, maxLength(50)),
  },
  name: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  vatCategory: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  materialType: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
  mwAccountGroup: { required: helpers.withMessage(`${$t('messages.errors.required')}`, required) },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

const selectedTab = ref('properties');

const currentComponent = computed(() => {
  const selected = section1Tabs.value.find((tab) => tab.name === selectedTab.value);
  return selected ? selected.component : null;
});

const isModified = ref<boolean>(false);

watch(
  () => modifiedMaterial.value,
  (newData) => {
    isModified.value = JSON.stringify(newData) !== JSON.stringify(material.value);
    validationModel.value.company = newData?.companyId || '';
    validationModel.value.code = newData?.code || '';
    validationModel.value.name = newData?.name || '';
    validationModel.value.vatCategory = newData?.vatCategoryId || '';
    validationModel.value.materialType = newData?.materialTypeId || '';
    validationModel.value.mwAccountGroup = newData?.mwAccountGroupId || '';
  },
  { deep: true }
);

const confirmModalActive = ref(false);
const isBackMode = ref(false);

function onCancelEdit(backMode: string) {
  if (isModified.value) {
    confirmModalActive.value = true;
    if (backMode == 'back') {
      isBackMode.value = true;
    }
    return;
  } else if (!material.value?.createdDate) {
    router.push('/materials-management');
  }
  mode.value = 'view';
}

async function onDiscardChanges() {
  mode.value = 'view';
  confirmModalActive.value = false;
  modifiedMaterial.value = material.value ? deepCopy<Material>(material.value) : undefined;
  resetTablesState.value = true;
  if (!material.value?.createdDate || isBackMode.value) {
    router.push('/materials-management');
  }
}

const toastStore = useToastStore();
async function onSaveChanges() {
  $v.value.$touch();
  if ($v.value.$invalid) return;

  mode.value = 'view';
  if (
    isModified.value &&
    modifiedMaterial.value &&
    (await addOrEditMaterial(modifiedMaterial.value))
  ) {
    await loadData();
    resetTablesState.value = true;
    toastStore.success({ text: `${$t('common.toast.success')}` });
  } else {
    toastStore.error({ text: `${$t('common.toast.error')}` });
  }
}

const selectedDetailsTab = ref('companyAlterCodes');

const detailsTabs = ref([
  {
    name: 'companyAlterCodes',
    label: $t('material.companyAlterCodes'),
  },
  {
    name: 'competitorAlterCodes',
    label: $t('material.competitorAlterCodes'),
  },
]);

const currentDetailsComponent = computed(() => {
  const selected = detailsTabs.value.find((tab) => tab.name === selectedDetailsTab.value);
  return selected ? selected : null;
});

const scrollbarId = ref<PerfectScrollbarExpose>();

function onSaveMaterialAlterCode(items: MaterialAlterCode[]) {
  if (!modifiedMaterial.value) return;
  if (!Array.isArray(modifiedMaterial.value.materialAlterCodes)) {
    modifiedMaterial.value.materialAlterCodes = [];
  }
  items.forEach((newItem) => {
    const index = modifiedMaterial.value?.materialAlterCodes?.findIndex(
      (existingItem) => existingItem.id === newItem.id
    );

    // Edge case: Handle new items that were removed (recordMode = 3)
    if (newItem.recordMode === 3) {
      if (index !== -1 && index !== undefined && modifiedMaterial.value?.materialAlterCodes) {
        modifiedMaterial.value.materialAlterCodes.splice(index, 1);
      } else {
        if (modifiedMaterial.value?.materialAlterCodes)
          modifiedMaterial.value.materialAlterCodes.push(newItem);
      }
    } else {
      if (index !== -1 && index !== undefined && modifiedMaterial.value?.materialAlterCodes) {
        modifiedMaterial.value.materialAlterCodes[index] = newItem;
      } else {
        if (modifiedMaterial.value?.materialAlterCodes)
          modifiedMaterial.value.materialAlterCodes.push(newItem);
      }
    }
  });
}

function onSaveCompetitorMaterials(items: CompetitorMaterial[]) {
  if (!modifiedMaterial.value) return;
  if (!Array.isArray(modifiedMaterial.value.competitorMaterials))
    modifiedMaterial.value.competitorMaterials = [];

  items.forEach((newItem) => {
    const index = modifiedMaterial.value?.competitorMaterials?.findIndex(
      (existingItem) => existingItem.id === newItem.id
    );

    if (newItem.recordMode === 3) {
      if (index !== -1 && index !== undefined && modifiedMaterial.value?.competitorMaterials) {
        modifiedMaterial.value?.competitorMaterials.splice(index, 1);
      } else {
        if (modifiedMaterial.value?.competitorMaterials)
          modifiedMaterial.value?.competitorMaterials?.push(newItem);
      }
    } else {
      if (index !== -1 && index !== undefined && modifiedMaterial.value?.competitorMaterials) {
        modifiedMaterial.value.competitorMaterials[index] = newItem;
      } else {
        if (modifiedMaterial.value?.competitorMaterials)
          modifiedMaterial.value.competitorMaterials.push(newItem);
      }
    }
  });
}

function onEditMode() {
  mode.value = 'edit';
  resetTablesState.value = false;
}
</script>
